import { isAxiosError } from 'axios';

import { CreateCampaignRejectedError, CreateCampaignResult } from './campaign-management-api.errors';
import http from './campaign-management-api.http';
import { CampaignManagementApi } from './campaign-management-api.types';

export { CreateCampaignRejectedError } from './campaign-management-api.errors';
export type { CreateCampaignResult } from './campaign-management-api.errors';

export default {
  /**
   * Activate an incentive (UNDER_ANALYSIS -> ACTIVE)
   * POST /incentives/{incentive_id}/activate
   *
   * Errors (403/404/409/...) are surfaced as plain AxiosErrors, same as every other
   * incentive method here — `parseIncentiveApiError` (features/incentives/utils.ts)
   * is the single place responsible for turning those into UI-friendly messages.
   */
  activateIncentive: async (
    incentiveId: string,
  ): Promise<CampaignManagementApi.IncentiveDetail> => {
    const { data } = await http.post<CampaignManagementApi.ActivateIncentiveResponse>(
      `/incentives/${incentiveId}/activate`,
    );

    return data.data;
  },

  /**
   * Archive an incentive (terminal state)
   * DELETE /incentives/{incentive_id}
   */
  archiveIncentive: async (
    incentiveId: string,
  ): Promise<CampaignManagementApi.IncentiveDetail> => {
    const { data } = await http.delete<CampaignManagementApi.ArchiveIncentiveResponse>(
      `/incentives/${incentiveId}`,
    );

    return data.data;
  },

  /**
   * Create a revalidation campaign
   * POST /campaigns
   */
  createCampaign: async (
    body: CampaignManagementApi.CreateCampaignRequest,
  ): Promise<CreateCampaignResult> => {
    try {
      const response = await http.post<CampaignManagementApi.CreateCampaignResponse>(
        '/campaigns',
        body,
      );

      return {
        data: response.data.data,
        status: response.status as 202 | 207,
      };
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const responseData = error.response.data as CampaignManagementApi.CreateCampaignErrorResponse;

        throw new CreateCampaignRejectedError(
          responseData.message ?? 'Não foi possível criar a campanha.',
          responseData.data,
        );
      }

      throw error;
    }
  },

  /**
   * Create an incentive
   * POST /incentives
   */
  createIncentive: async (
    body: CampaignManagementApi.CreateIncentiveRequest,
  ): Promise<CampaignManagementApi.IncentiveDetail> => {
    const { data } = await http.post<CampaignManagementApi.CreateIncentiveResponse>(
      '/incentives',
      body,
    );

    return data.data;
  },

  /**
   * Get campaign by id
   * GET /campaigns/{campaign_id}
   */
  getCampaign: async (
    campaignId: string,
  ): Promise<CampaignManagementApi.CampaignDetailData> => {
    const { data } = await http.get<CampaignManagementApi.GetCampaignResponse>(
      `/campaigns/${campaignId}`,
    );

    return data.data;
  },

  /**
   * Get incentive by id
   * GET /incentives/{incentive_id}
   */
  getIncentive: async (
    incentiveId: string,
  ): Promise<CampaignManagementApi.IncentiveDetail> => {
    const { data } = await http.get<CampaignManagementApi.GetIncentiveResponse>(
      `/incentives/${incentiveId}`,
    );

    return data.data;
  },

  /**
   * Get an incentive banner image by its external id
   * GET /incentives/banner/{banner_file_external_id}
   */
  getIncentiveBanner: async (
    bannerFileExternalId: string,
  ): Promise<CampaignManagementApi.IncentiveBannerData> => {
    const { data } = await http.get<CampaignManagementApi.GetIncentiveBannerResponse>(
      `/incentives/banner/${bannerFileExternalId}`,
    );

    return data.data;
  },

  /**
   * Get target campaign journey
   * GET /targets/{target_id}/campaign-journey
   */
  getTargetCampaignJourney: async (
    targetId: string,
    params: CampaignManagementApi.GetTargetCampaignJourneyParams = {},
  ): Promise<CampaignManagementApi.TargetCampaignJourneyData> => {
    const { data } = await http.get<CampaignManagementApi.GetTargetCampaignJourneyResponse>(
      `/targets/${targetId}/campaign-journey`,
      { params },
    );

    return data.data;
  },

  /**
   * List campaigns with pagination and optional filters
   * GET /campaigns
   */
  listCampaigns: async (
    params: CampaignManagementApi.ListCampaignsParams = {},
  ): Promise<CampaignManagementApi.ListCampaignsData> => {
    const { data } = await http.get<CampaignManagementApi.ListCampaignsResponse>(
      '/campaigns',
      { params },
    );

    return data.data;
  },

  /**
   * List campaign targets (partner performance)
   * GET /campaigns/{campaign_id}/targets
   */
  listCampaignTargets: async (
    campaignId: string,
    params: CampaignManagementApi.ListCampaignTargetsParams = {},
  ): Promise<CampaignManagementApi.CampaignTargetsData> => {
    const { data } = await http.get<CampaignManagementApi.GetCampaignTargetsResponse>(
      `/campaigns/${campaignId}/targets`,
      { params },
    );

    return data.data;
  },

  /**
   * List incentives with pagination
   * GET /incentives
   */
  listIncentives: async (
    params: CampaignManagementApi.ListIncentivesParams = {},
  ): Promise<CampaignManagementApi.ListIncentivesData> => {
    const { data } = await http.get<CampaignManagementApi.ListIncentivesResponse>(
      '/incentives',
      { params },
    );

    return data.data;
  },

  /**
   * List target campaigns (partner view) with optional filters
   * GET /targets/campaigns
   */
  listTargets: async (
    params: CampaignManagementApi.ListTargetsParams = {},
  ): Promise<CampaignManagementApi.ListTargetsData> => {
    const { data } = await http.get<CampaignManagementApi.ListTargetsResponse>(
      '/targets/campaigns',
      {
        params: {
          target_type: 'PARTNER',
          ...params,
        },
      },
    );

    return data.data;
  },

  /**
   * Update an incentive (any successful PATCH moves status back to UNDER_ANALYSIS)
   * PATCH /incentives/{incentive_id}
   */
  updateIncentive: async (
    incentiveId: string,
    body: CampaignManagementApi.UpdateIncentiveRequest,
  ): Promise<CampaignManagementApi.IncentiveDetail> => {
    const { data } = await http.patch<CampaignManagementApi.UpdateIncentiveResponse>(
      `/incentives/${incentiveId}`,
      body,
    );

    return data.data;
  },

  /**
   * Upload an incentive banner file
   * POST /incentives/banner
   */
  uploadIncentiveBanner: async (
    file: File,
    onProgress?: (_percent: number) => void,
  ): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await http.post<CampaignManagementApi.UploadIncentiveBannerResponse>(
      '/incentives/banner',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (!onProgress || !progressEvent.total) return;
          onProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        },
      },
    );

    return data.data.banner_file_external_id;
  },
};
